import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  MapPin,
  Users,
  Building2,
  Eye
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/api';
import usePersistentFilters from '../../hooks/usePersistentFilters';

const RegionalOffices = () => {
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  
  const defaultFilters = {
    search: '',
    isActive: '',
    page: 1
  };

  const {
    filters,
    handleFilterChange,
    handlePageChange,
    clearFilters
  } = usePersistentFilters(defaultFilters, 'regional-offices-filters');

  const handleSearchChange = (e) => {
    handleFilterChange('search', e.target.value);
  };

  const { data, isLoading, error } = useQuery(
    ['regional-offices', filters],
    () => api.get('/regional-offices', { params: filters }),
    {
      select: (data) => data?.data?.data || data?.data,
      keepPreviousData: true
    }
  );

  const deleteMutation = useMutation(
    (officeId) => api.delete(`/regional-offices/${officeId}`),
    {
      onSuccess: () => {
        toast.success('Regional office deleted successfully');
        queryClient.invalidateQueries(['regional-offices']);
      },
      onError: (error) => {
        toast.error(error?.response?.data?.message || 'Failed to delete regional office');
      }
    }
  );

  const handleDeleteOffice = async (officeId) => {
    if (window.confirm('Are you sure you want to delete this regional office?')) {
      deleteMutation.mutate(officeId);
    }
  };

  const offices = data?.offices || [];
  const pagination = data?.pagination || {};

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <span className="badge badge-success">Active</span>
    ) : (
      <span className="badge badge-danger">Inactive</span>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="empty-state">
        <Building2 className="empty-state-icon" />
        <h3 className="empty-state-title">Failed to load regional offices</h3>
        <p className="empty-state-description">
          {error?.response?.data?.message || 'An error occurred while loading regional offices'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="page-header">
        <div className="px-4 py-5 sm:px-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="page-title">Regional Offices</h1>
              <p className="page-subtitle">
                Manage regional offices and their serving areas
              </p>
            </div>
            {hasPermission('regional_offices', 'write') && (
              <Link to="/regional-offices/create" className="btn btn-primary">
                <Plus className="h-4 w-4 mr-2" />
                Add Office
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <h3 className="card-title">Filters</h3>
            <button
              onClick={clearFilters}
              className="btn btn-outline btn-sm"
            >
              Clear Filters
            </button>
          </div>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div>
              <label className="form-label">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, address..."
                  value={filters.search}
                  onChange={handleSearchChange}
                  className="input pl-10"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <label className="form-label">Status</label>
              <select
                value={filters.isActive}
                onChange={(e) => handleFilterChange('isActive', e.target.value)}
                className="input"
              >
                <option value="">All Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Offices List */}
      <div className="card">
        <div className="card-body">
          {offices.length === 0 ? (
            <div className="empty-state">
              <Building2 className="empty-state-icon" />
              <h3 className="empty-state-title">No regional offices found</h3>
              <p className="empty-state-description">
                {filters.search || filters.isActive
                  ? 'Try adjusting your search criteria'
                  : 'Get started by creating your first regional office'}
              </p>
              {hasPermission('regional_offices', 'write') && (
                <Link to="/regional-offices/create" className="btn btn-primary mt-4">
                  <Plus className="h-4 w-4 mr-2" />
                  Create First Office
                </Link>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Office Name</th>
                    <th>Location</th>
                    <th>Admin</th>
                    <th>Serving Areas</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>View</th>
                    <th>Edit</th>
                    <th>Remove</th>
                  </tr>
                </thead>
                <tbody>
                  {offices.map((office) => (
                    <tr key={office._id}>
                      <td>
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                            <Building2 className="h-5 w-5 text-primary-600" />
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">{office.name}</div>
                            <div className="text-sm text-gray-500">{office.address}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center space-x-2">
                          <MapPin className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-600">
                            {office.location?.coordinates?.join(', ')}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="text-sm">
                          <div className="font-medium text-gray-900">
                            {office.adminUser?.name || 'N/A'}
                          </div>
                          <div className="text-gray-500">
                            {office.adminUser?.email || 'N/A'}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center space-x-2">
                          <Users className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-600">
                            {office.servingAreas?.length || 0} areas
                          </span>
                        </div>
                      </td>
                      <td>{getStatusBadge(office.isActive)}</td>
                      <td>
                        <div className="text-sm text-gray-500">
                          {new Date(office.createdOn).toLocaleDateString()}
                        </div>
                      </td>
                      <td>
                        <Link
                          to={`/regional-offices/${office._id}`}
                          className="btn btn-sm btn-secondary"
                          title="View"
                        >
                          <Eye className="h-4 w-4" />
                        </Link>
                      </td>
                      <td>
                        {hasPermission('regional_offices', 'write') ? (
                          <Link
                            to={`/regional-offices/${office._id}/edit`}
                            className="btn btn-sm btn-info"
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </Link>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td>
                        {hasPermission('regional_offices', 'write') ? (
                          <button
                            onClick={() => handleDeleteOffice(office._id)}
                            className="btn btn-sm btn-danger"
                            disabled={deleteMutation.isLoading}
                            title="Remove"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pagination.total > 1 && (
            <div className="flex items-center justify-between mt-6">
              <div className="text-sm text-gray-500">
                Showing {((pagination.current - 1) * pagination.limit) + 1} to{' '}
                {Math.min(pagination.current * pagination.limit, pagination.totalRecords)} of{' '}
                {pagination.totalRecords} results
              </div>
              
              <div className="flex space-x-2">
                <button
                  onClick={() => handlePageChange(pagination.current - 1)}
                  disabled={pagination.current === 1}
                  className="btn btn-sm btn-secondary disabled:opacity-50"
                >
                  Previous
                </button>
                
                <span className="flex items-center px-3 py-2 text-sm text-gray-500">
                  Page {pagination.current} of {pagination.total}
                </span>
                
                <button
                  onClick={() => handlePageChange(pagination.current + 1)}
                  disabled={pagination.current === pagination.total}
                  className="btn btn-sm btn-secondary disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegionalOffices; 